import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loading, Loader } from 'components/elements/Loading';
import EmptyStatePlaceHolder from 'components/widgets/Dashboard/CompanyGroups/EmptyStatePlaceholder';
import GroupDetails from 'components/widgets/Dashboard/CompanyGroups/GroupDetails';
import ImportCompaniesFlow from 'components/widgets/Dashboard/CompanyGroups/ImportCompaniesFlow';
import GroupConfigMenu from 'components/widgets/Dashboard/GroupConfigMenu';
import Layout from 'components/widgets/Dashboard/Layout';
import { useGroupConfigurations } from 'hooks/useGroupConfig';
import { useUser } from 'state/auth';
import { useDashboardGroupType } from 'state/dashboard';
import { useSelectedGroupQuery } from 'state/queryParams';
import { trackPageView } from 'utils/analytics';
import {
  CompanyGroupsPageViewSections,
  CompanyGroupsPageViewSubSections,
} from 'utils/analytics/constants';
import { GROUP_TYPE } from 'utils/constants';
import { QUERY_PARAM } from 'utils/constants/QueryParameters';

type TDetailsProps = {
  isLoading: boolean;
  hasGroups: boolean;
  groupId?: string;
  showImportFlow: boolean;
  groupName?: string;
};

const Details = ({
  isLoading,
  hasGroups,
  groupId,
  showImportFlow,
  groupName,
}: TDetailsProps) => {
  if (isLoading) {
    return <Loader />;
  }

  if (!hasGroups) {
    return <EmptyStatePlaceHolder />;
  }

  if (!groupId) {
    return <Loader />;
  }

  if (showImportFlow) {
    return <ImportCompaniesFlow groupId={groupId} groupName={groupName ?? 'Group'} />;
  }

  return <GroupDetails />;
};

const CompanyGroups = () => {
  const [searchParams] = useSearchParams();
  const [, setType] = useDashboardGroupType();
  const [group] = useSelectedGroupQuery();
  const { user } = useUser();
  const [selectedGroupId, setSelectedGroupId] = useState<string>();
  const showImportFlow = searchParams.get(QUERY_PARAM.importCompanies) === 'companies';

  const {
    data: groupList,
    isLoading,
    isFetching,
  } = useGroupConfigurations({
    userId: String(user?.id),
    groupType: GROUP_TYPE.COMPANY,
    keepPreviousData: false,
  });

  useEffect(() => {
    setType(GROUP_TYPE.COMPANY);
  }, [setType]);

  useEffect(() => {
    if (!isLoading && groupList) {
      if (groupList.length > 0 && !group) {
        // There are company groups but no group selected yet; wait for selection
        return;
      }

      const isHome = !groupList.length && !group;
      const isCompanyGroup =
        group && groupList.find(({ id }) => String(id) === group)?.id;
      if (!!isHome || !!isCompanyGroup) {
        const subSectionName = isHome
          ? CompanyGroupsPageViewSubSections.HOME
          : String(group);

        trackPageView({
          sectionName: CompanyGroupsPageViewSections.COMPANY_GROUPS,
          subSectionName,
        });
      }
    }
  }, [isLoading, group]);

  const handleGroupIdSelection = (selectedId: string) => {
    setSelectedGroupId(selectedId);
  };

  return (
    <Layout
      menuContent={
        <GroupConfigMenu
          isLoading={isLoading}
          isFetching={isFetching}
          groupList={groupList}
          groupType={GROUP_TYPE.COMPANY}
          onSelectGroupId={handleGroupIdSelection}
        />
      }
      detailContent={
        <Loading>
          <Details
            isLoading={isLoading}
            hasGroups={!!groupList?.length}
            groupId={selectedGroupId}
            showImportFlow={showImportFlow}
            groupName={groupList?.find((g) => String(g.id) === selectedGroupId)?.name}
          />
        </Loading>
      }
    />
  );
};

export default CompanyGroups;
